import React, { useEffect, useState } from "react";
import {
  FileText,
  Building2,
  Box,
  CalendarDays,
  CheckCircle2,
  XCircle,
  IndianRupee,
  MoreHorizontal,
  Download,
  Filter,
  Trash2,
  Trash,
  EyeIcon,
  Loader2,
} from "lucide-react";
import {
  deleteSoftwareQuotation,
  viewSoftwareQuotationByID,
} from "../../../../services/quotationService";
import { showErrorToast, showSuccessToast } from "../../../../utils/toastUtils";
import ConfirmModal from "../../../../components/Billing/ConfirmModal";
import ViewSoftwareQuotation from "./ViewSoftwareQuotation";
import QuotationReportModal from "./ViewSoftwareQuotation";

// Helper function to format currency (INR)
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    draft: "bg-gray-100 text-gray-700 border-gray-200",
    pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
    approved: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
  };

  const style = statusStyles[status?.toLowerCase()] || statusStyles.draft;

  return (
    <span
      className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${style}`}
    >
      {status}
    </span>
  );
};

function SoftwareQuotationReport({ reportData = [], setFetchAgain }) {
  const [individualQuotationData, setIndividualQuotationData] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentIdForDeleting, setCurrentIdForDeleting] = useState("");
  const [currentIdForUpdate, setCurrentIdForUpdate] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [clickedRowId, setClickedRowId] = useState("");

  const handleViewQuotation = async (id) => {
    try {
      setViewLoading(true);
      setClickedRowId(id);
      const res = await viewSoftwareQuotationByID(id);
      if (res) {
        console.log(res);
      }
      if (res?.status?.code === 200) {
        setShowViewModal(true);
        setIndividualQuotationData(res?.data?.quotation);
      } else {
        showErrorToast(
          res?.message ||
            res?.status?.message ||
            res?.data?.message ||
            res?.data?.status?.message ||
            "Something Went Wrong",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setViewLoading(false);
      setClickedRowId("");
    }
  };

  const handleConfirmation = (id) => {
    setCurrentIdForDeleting(id);
    setShowConfirmationModal(true);
  };

  const handleDeleteQuotation = async () => {
    try {
      setDeleteLoading(true);
      const res = await deleteSoftwareQuotation(currentIdForDeleting);
      if (res) {
        console.log(res);
      }
      if (res?.status?.code === 200) {
        showSuccessToast(
          res?.message ||
            res?.data?.message ||
            res?.status?.message ||
            "Deleted Successfully",
        );
        setFetchAgain();
        setShowConfirmationModal(false);
        setCurrentIdForDeleting("");
      } else {
        showErrorToast(res?.message || res?.data?.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div id="softwareQuotationReport" className="w-full bg-white rounded-xl mt-6 shadow-sm border border-gray-200 overflow-hidden font-sans">
      {/* Header Section */}
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row justify-start items-start sm:items-center gap-4">
         <div className="p-2 bg-blue-50 text-green-600 rounded-lg">
                        <FileText size={28} />
                      </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Quotation Reports
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage and track your software project quotations.
          </p>
        </div>
        <div className="flex gap-3 hidden">
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Filter size={16} />
            Filters
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-200">
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Quotation Details
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Client & Software
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                API Integrations
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Timeline
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Net Total
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {reportData?.length > 0 &&
              reportData.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50/50 transition-colors group"
                >
                  {/* Quotation Details */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                        <FileText size={18} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {row.quotation_no}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(row.quotation_date)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Client & Software */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5 text-gray-900 font-medium">
                        <Building2 size={14} className="text-gray-400" />
                        {row.client_name}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <Box size={14} className="text-gray-400" />
                        {row.software_type}
                      </div>
                    </div>
                  </td>

                  {/* API Integrations */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1.5 text-sm">
                      <div className="flex items-center gap-2">
                        {row.has_idspay_api ? (
                          <CheckCircle2 size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-gray-300" />
                        )}
                        <span
                          className={
                            row.has_idspay_api
                              ? "text-gray-700"
                              : "text-gray-400"
                          }
                        >
                          IDsPay API
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {row.has_third_party_api ? (
                          <CheckCircle2 size={16} className="text-green-500" />
                        ) : (
                          <XCircle size={16} className="text-gray-300" />
                        )}
                        <span
                          className={
                            row.has_third_party_api
                              ? "text-gray-700"
                              : "text-gray-400"
                          }
                        >
                          Third Party API
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Timeline */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CalendarDays size={16} className="text-gray-400" />
                      Delivery:{" "}
                      <span className="font-medium">
                        {formatDate(row.software_delivery_date)}
                      </span>
                    </div>
                  </td>

                  {/* Net Total */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="flex items-center text-gray-900 font-semibold">
                        <IndianRupee size={14} className="mr-0.5" />
                        {formatCurrency(row.net_total_cost).replace("₹", "")}
                      </div>
                      {parseFloat(row.discount_applicable) > 0 && (
                        <div className="text-xs text-green-600">
                          Includes {formatCurrency(row.discount_applicable)}{" "}
                          discount
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={row.status} />
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* View Button */}
                      <button
                        onClick={() => handleViewQuotation(row.id)}
                        title="View Details"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 active:scale-95"
                      >
                        {viewLoading && clickedRowId === row?.id ? (
                          <Loader2
                            size={18}
                            strokeWidth={2}
                            className="animate-spin text-blue-600"
                          />
                        ) : (
                          <EyeIcon size={18} strokeWidth={2} />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleConfirmation(row.id)}
                        title="Delete Record"
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500/30 active:scale-95"
                      >
                        <Trash size={18} strokeWidth={2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Empty State */}
        {(!reportData || reportData.length === 0) && (
          <div className="p-12 text-center text-gray-500">
            No quotation reports found.
          </div>
        )}
      </div>

      {/* Pagination / Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <span className="text-sm text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-900">{reportData.length}</span>{" "}
          results
        </span>
      </div>

      {showConfirmationModal && (
        <ConfirmModal
          isOpen={showConfirmationModal}
          onCancel={() => setShowConfirmationModal(false)}
          onConfirm={handleDeleteQuotation}
          loading={deleteLoading}
        />
      )}

      {showViewModal && (
        <QuotationReportModal
          isOpen={showViewModal}
          quotation={individualQuotationData}
          onClose={() => {
            setIndividualQuotationData(null);
            setShowViewModal(false);
          }}
        />
      )}
    </div>
  );
}

export default SoftwareQuotationReport;
